// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { NetworkSource } from "@lib/messaging/NetworkSource";
import { JsonRpcID } from "@lib/messaging/rpc/jsonRpc/JsonRpcID";
import { JsonRpcPendingCall } from "@lib/messaging/rpc/jsonRpc/JsonRpcPendingCall";
import { Random } from "@lib/utilities/Random";

/**
 * Provides methods for managing pending JSON-RPC calls.
 * Completed calls are automatically removed from the container.
 */
export class JsonRpcPendingCallContainer<T, E>
{
  private readonly pendingCalls: Dictionary<JsonRpcPendingCall<T, E>> = {};

  add(target: NetworkSource, method: string): JsonRpcPendingCall<T, E>
  {
    const id = Random.generateRequestID();
    const pendingCall = new JsonRpcPendingCall<T, E>({
      id: id,
      method: method,
      target: target
    });

    pendingCall.promise.then(() =>
    {
      delete this.pendingCalls[id];
    }, () =>
      {
        delete this.pendingCalls[id];
      });

    this.pendingCalls[pendingCall.id] = pendingCall;
    return pendingCall;
  }

  contains(id: JsonRpcID): boolean
  {
    return id !== null && id in this.pendingCalls;
  }

  remove(id: JsonRpcID): Nullable<JsonRpcPendingCall<T, E>>
  {
    if (id === null)
      return null;

    const pendingCall = this.pendingCalls[id];
    if (pendingCall)
    {
      delete this.pendingCalls[id];
      return pendingCall;
    }

    return null;
  }
}